/**
 * Fisher-Yates shuffle.
 */
function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/**
 * Non-repeating card queue: every card is seen once before any repeat.
 * When the queue is empty, reshuffles and ensures the first card of the new
 * shuffle is NOT the same as the last shown card.
 */
export function createCardQueue() {
  let queue: number[] = [];
  let lastShownIndex: number | null = null;

  function ensureQueue(getPoolIndices: () => number[]) {
    if (queue.length > 0) return;
    const indices = getPoolIndices();
    if (indices.length === 0) return;
    queue = shuffleArray(indices);
    if (lastShownIndex !== null && queue[0] === lastShownIndex && indices.length > 1) {
      const swapIdx = queue.findIndex((i) => i !== lastShownIndex);
      if (swapIdx >= 0) {
        [queue[0], queue[swapIdx]] = [queue[swapIdx], queue[0]];
      }
    }
  }

  function getNext(
    cards: { length: number; [i: number]: unknown },
    getPoolIndices: () => number[]
  ): number | null {
    const indices = getPoolIndices();
    if (indices.length === 0) return null;
    const validSet = new Set(indices);
    while (true) {
      ensureQueue(getPoolIndices);
      if (queue.length === 0) return null;
      const idx = queue.shift()!;
      if (validSet.has(idx) && idx >= 0 && idx < cards.length) {
        lastShownIndex = idx;
        return idx;
      }
    }
  }

  function reset() {
    queue = [];
    lastShownIndex = null;
  }

  return { getNext, reset };
}
